import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
    Box, Typography, Paper, Grid, Stack, IconButton, Button, Chip,
    TextField, InputAdornment, LinearProgress, Tooltip,
    Popover,
} from "@mui/material";
import {
    Search, Refresh, FilterList, Close, Download,
    PhoneEnabled, PhoneMissed, PhoneInTalk, CheckCircle,
    AccessTime, TrendingUp, TrendingDown, EmojiEvents,
    PersonSearch, Insights, LeaderboardOutlined,
    BusinessCenter, Star, Groups,
} from "@mui/icons-material";
import { alpha, styled } from "@mui/material/styles";
import { supabase } from "../../supabaseClient";
import SMDetailDrawer from "./SMDetailDrawer";

/* ── Design tokens (aligned with app primary palette) ── */
const SM = {
    sans: "'Inter', system-ui, sans-serif",
    mono: "'Geist Mono', monospace",
    teal: "#4338ca",    tealSoft: "#eef2ff",   tealBd: "#c7d2fe",   // indigo primary
    coral: "#f97316",   coralSoft: "#fff7ed",  coralBd: "#fed7aa",  // orange accent
    purple: "#7c3aed",  purpleSoft: "#f5f3ff", purpleBd: "#ddd6fe",
    cyan: "#0284c7",    cyanSoft: "#f0f9ff",   cyanBd: "#bae6fd",   // sky blue
    green: "#16a34a",   greenSoft: "#f0fdf4",  greenBd: "#bbf7d0",
    red: "#dc2626",     redSoft: "#fef2f2",    redBd: "#fecaca",
    amber: "#d97706",   amberSoft: "#fffbeb",  amberBd: "#fde68a",
    page: "#f8fafc",    white: "#ffffff",
    border: "#e2e8f0",  border2: "#f1f5f9",
    txt1: "#0f172a",    txt2: "#334155",
    txt3: "#64748b",    txt4: "#94a3b8",
};

/* ── Helpers ── */
const fmtDur = s => { if (!s) return "—"; const m = Math.floor(s / 60), sec = Math.floor(s % 60); return m ? `${m}m ${sec}s` : `${sec}s`; };
const fmtTalkTime = s => { if (!s) return "0m"; const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60); return h ? `${h}h ${m}m` : `${m}m`; };
const pct = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0;
const AV_COLORS = ["#4338ca", "#f97316", "#7c3aed", "#0284c7", "#ec4899", "#d97706"];
const avColor = n => { if (!n) return SM.txt4; let h = 0; for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) & 0xffffffff; return AV_COLORS[Math.abs(h) % AV_COLORS.length]; };
const initials = n => { if (!n) return "?"; const p = n.trim().split(" "); return (p.length >= 2 ? p[0][0] + p[1][0] : p[0].slice(0, 2)).toUpperCase(); };
const scoreColor = n => n >= 70 ? SM.green : n >= 40 ? SM.amber : SM.red;
const scoreSoft  = n => n >= 70 ? SM.greenSoft : n >= 40 ? SM.amberSoft : SM.redSoft;

/* ── Date range helpers ── */
const DATE_PRESETS = [
    { key: "today",     label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "thisWeek",  label: "This Week" },
    { key: "lastWeek",  label: "Last Week" },
    { key: "thisMonth", label: "This Month" },
    { key: "lastMonth", label: "Last Month" },
    { key: "custom",    label: "Custom" },
];

function getDateRange(preset, customFrom, customTo) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (preset) {
        case "today": {
            const eod = new Date(today); eod.setHours(23, 59, 59, 999);
            return { from: today.getTime(), to: eod.getTime() };
        }
        case "yesterday": {
            const y = new Date(today); y.setDate(y.getDate() - 1);
            return { from: y.getTime(), to: today.getTime() - 1 };
        }
        case "thisWeek": {
            const dow = today.getDay();
            const mon = new Date(today); mon.setDate(today.getDate() - ((dow + 6) % 7));
            return { from: mon.getTime(), to: now.getTime() };
        }
        case "lastWeek": {
            const dow = today.getDay();
            const mon = new Date(today); mon.setDate(today.getDate() - ((dow + 6) % 7) - 7);
            const sun = new Date(mon); sun.setDate(mon.getDate() + 7);
            return { from: mon.getTime(), to: sun.getTime() - 1 };
        }
        case "thisMonth": {
            const m = new Date(today.getFullYear(), today.getMonth(), 1);
            return { from: m.getTime(), to: now.getTime() };
        }
        case "lastMonth": {
            const m = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const e = new Date(today.getFullYear(), today.getMonth(), 1);
            return { from: m.getTime(), to: e.getTime() - 1 };
        }
        case "custom": {
            const from = customFrom ? new Date(`${customFrom}T00:00:00`).getTime() : 0;
            const to   = customTo   ? new Date(`${customTo}T23:59:59`).getTime()   : now.getTime();
            return { from, to };
        }
        default:
            return { from: today.getTime(), to: now.getTime() };
    }
}

/* ── Styled components ── */
const Surface = styled(Paper)({
    background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,.05)",
});

const SMCard = styled(Paper)({
    background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,.04)", cursor: "pointer", overflow: "hidden",
    transition: "transform .15s, box-shadow .15s",
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(0,0,0,.09)" },
});

/* ══════════════════════════════════════════════════════
   DATE RANGE BAR
══════════════════════════════════════════════════════ */
function DateRangeBar({ preset, onPreset, customFrom, customTo, onCustomFrom, onCustomTo }) {
    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
            {DATE_PRESETS.map(p => (
                <Chip key={p.key} label={p.label} size="small" clickable
                    onClick={() => onPreset(p.key)}
                    sx={{
                        fontSize: 12, fontFamily: SM.sans, fontWeight: 600,
                        background: preset === p.key ? SM.teal : SM.white,
                        color: preset === p.key ? "#fff" : SM.txt2,
                        border: `1px solid ${preset === p.key ? SM.teal : SM.border}`,
                        "&:hover": { background: preset === p.key ? SM.teal : SM.tealSoft, borderColor: SM.teal },
                    }}
                />
            ))}
            {preset === "custom" && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", gap: 1 }}>
                    <TextField type="date" size="small" value={customFrom} onChange={e => onCustomFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }} label="From"
                        sx={{ width: 150, "& .MuiInputBase-input": { fontSize: 12, fontFamily: SM.sans } }} />
                    <TextField type="date" size="small" value={customTo} onChange={e => onCustomTo(e.target.value)}
                        InputLabelProps={{ shrink: true }} label="To"
                        sx={{ width: 150, "& .MuiInputBase-input": { fontSize: 12, fontFamily: SM.sans } }} />
                </Stack>
            )}
        </Box>
    );
}

/* ══════════════════════════════════════════════════════
   KPI CARDS
══════════════════════════════════════════════════════ */
function SMKpiCards({ overall, loading }) {
    const cards = [
        { label: "Total Calls",        value: overall.totCalls,              color: SM.teal,   soft: SM.tealSoft,   Icon: PhoneEnabled },
        { label: "Answered",           value: overall.totAns,                color: SM.green,  soft: SM.greenSoft,  Icon: CheckCircle },
        { label: "Missed",             value: overall.totMiss,               color: SM.red,    soft: SM.redSoft,    Icon: PhoneMissed },
        { label: "Not Answered",       value: overall.totNotAns,             color: SM.amber,  soft: SM.amberSoft,  Icon: PhoneMissed },
        { label: "Connected",          value: overall.totConn,               color: SM.cyan,   soft: SM.cyanSoft,   Icon: PhoneInTalk },
        { label: "Total Duration",     value: fmtTalkTime(overall.totDur),   color: SM.purple, soft: SM.purpleSoft, Icon: AccessTime },
        { label: "Avg Duration",       value: fmtDur(overall.avgDur),        color: SM.teal,   soft: SM.tealSoft,   Icon: AccessTime },
        { label: "Avg Bill Duration",  value: fmtDur(overall.avgBill),       color: SM.coral,  soft: SM.coralSoft,  Icon: AccessTime },
        { label: "Answer Rate",        value: `${overall.answerRate}%`,      color: SM.green,  soft: SM.greenSoft,  Icon: TrendingUp },
        { label: "Missed Rate",        value: `${overall.missedRate}%`,      color: SM.red,    soft: SM.redSoft,    Icon: TrendingDown },
        { label: "Most Active SM",     value: overall.mostActiveName,        color: SM.teal,   soft: SM.tealSoft,   Icon: PersonSearch },
        { label: "Best Performing SM", value: overall.bestPerfName,          color: SM.purple, soft: SM.purpleSoft, Icon: EmojiEvents },
        { label: "Highest Duration SM",value: overall.highDurName,           color: SM.coral,  soft: SM.coralSoft,  Icon: Star },
    ];
    return (
        <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: { xs: 2, sm: 2.5 } }}>
            {cards.map((c, i) => (
                <Grid item xs={6} sm={4} md={2} key={c.label}>
                    <Surface elevation={0} sx={{
                        p: { xs: "10px 10px", sm: "12px 14px" }, borderTop: `3px solid ${c.color}`, height: "100%",
                        transition: "transform .15s, box-shadow .15s",
                        "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(0,0,0,.08)" },
                    }}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.5}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{
                                    fontSize: { xs: 14, sm: 18 }, fontWeight: 800, color: SM.txt1,
                                    fontFamily: SM.mono, letterSpacing: "-0.5px", lineHeight: 1.1,
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                }}>
                                    {loading ? "—" : c.value}
                                </Typography>
                                <Typography sx={{ fontSize: { xs: 9.5, sm: 10.5 }, fontWeight: 600, color: SM.txt3, mt: 0.4, fontFamily: SM.sans, lineHeight: 1.3 }}>
                                    {c.label}
                                </Typography>
                            </Box>
                            <Box sx={{
                                width: { xs: 24, sm: 28 }, height: { xs: 24, sm: 28 }, borderRadius: 8,
                                background: c.soft, color: c.color, flexShrink: 0,
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                                <c.Icon sx={{ fontSize: { xs: 13, sm: 15 } }} />
                            </Box>
                        </Stack>
                    </Surface>
                </Grid>
            ))}
        </Grid>
    );
}

/* ══════════════════════════════════════════════════════
   SM PERFORMANCE CARDS
══════════════════════════════════════════════════════ */
function SMPerformanceCards({ smStats, onSelect, loading }) {
    const RANK_BADGE = { 1: "🥇", 2: "🥈", 3: "🥉" };
    if (loading) return (
        <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography sx={{ color: SM.txt4, fontSize: 13, fontFamily: SM.sans }}>Loading SM data…</Typography>
        </Box>
    );
    if (!smStats.length) return (
        <Box sx={{ py: 5, textAlign: "center" }}>
            <Groups sx={{ fontSize: 36, color: SM.border, mb: 1 }} />
            <Typography sx={{ color: SM.txt3, fontSize: 14, fontWeight: 500, fontFamily: SM.sans }}>No data for selected period</Typography>
        </Box>
    );
    return (
        <Box sx={{ mb: { xs: 2, sm: 2.5 } }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13, color: SM.txt1, mb: 1, fontFamily: SM.sans }}>
                Sales Manager Performance
            </Typography>
            <Grid container spacing={1}>
                {smStats.map(sm => (
                    <Grid item xs={12} sm={6} md={4} key={sm.userId}>
                        <SMCard elevation={0} onClick={() => onSelect(sm)}>
                            <Box sx={{ height: 3, background: sm.rank <= 3 ? `linear-gradient(90deg,${SM.teal},${SM.cyan})` : SM.border2 }} />
                            <Box sx={{ p: "10px 12px" }}>
                                {/* Header */}
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <Box sx={{
                                        width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                                        background: avColor(sm.agentName), color: "#fff",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: 11, fontWeight: 700, fontFamily: SM.sans,
                                    }}>{initials(sm.agentName)}</Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Typography sx={{
                                                fontWeight: 700, fontSize: 12.5, color: SM.txt1, fontFamily: SM.sans,
                                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                            }}>
                                                {sm.agentName}
                                            </Typography>
                                            {RANK_BADGE[sm.rank] && (
                                                <Typography sx={{ fontSize: 12, flexShrink: 0, lineHeight: 1 }}>{RANK_BADGE[sm.rank]}</Typography>
                                            )}
                                        </Stack>
                                        <Typography sx={{ fontSize: 10, color: SM.txt4, fontFamily: SM.sans }}>Rank #{sm.rank}</Typography>
                                    </Box>
                                    <Box sx={{
                                        px: "7px", py: "2px", borderRadius: 20, fontSize: 11, fontWeight: 700, flexShrink: 0,
                                        background: scoreSoft(sm.performanceScore), color: scoreColor(sm.performanceScore),
                                    }}>{sm.performanceScore}</Box>
                                </Stack>

                                {/* Compact inline stats */}
                                <Stack direction="row" spacing={0} sx={{ mb: 1, background: SM.page, borderRadius: 8, overflow: "hidden", border: `1px solid ${SM.border}` }}>
                                    {[
                                        { l: "Calls",    v: sm.totalCalls,   c: SM.teal },
                                        { l: "Ans",      v: sm.answered,     c: SM.green },
                                        { l: "Missed",   v: sm.missed,       c: SM.red },
                                        { l: "Score",    v: sm.avgLeadScore, c: SM.purple },
                                    ].map((s, i) => (
                                        <Box key={s.l} sx={{
                                            flex: 1, textAlign: "center", py: "5px",
                                            borderRight: i < 3 ? `1px solid ${SM.border}` : "none",
                                        }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: s.c, fontFamily: SM.mono, lineHeight: 1 }}>{s.v}</Typography>
                                            <Typography sx={{ fontSize: 9, color: SM.txt4, fontFamily: SM.sans, mt: 0.25 }}>{s.l}</Typography>
                                        </Box>
                                    ))}
                                </Stack>

                                {/* Answer rate bar */}
                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                                    <Typography sx={{ fontSize: 10, color: SM.txt4, fontFamily: SM.sans }}>Answer Rate</Typography>
                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: SM.teal, fontFamily: SM.sans }}>{sm.answerRate}%</Typography>
                                </Stack>
                                <Box sx={{ height: 4, borderRadius: 3, background: SM.border2, overflow: "hidden" }}>
                                    <Box sx={{
                                        height: "100%", width: `${sm.answerRate}%`,
                                        background: `linear-gradient(90deg,${SM.teal},${SM.cyan})`,
                                        borderRadius: 3, transition: "width .3s",
                                    }} />
                                </Box>
                            </Box>
                        </SMCard>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

/* ══════════════════════════════════════════════════════
   LEADERBOARD
══════════════════════════════════════════════════════ */
function SMLeaderboard({ smStats, onSelect }) {
    const MEDAL = { 1: "🥇", 2: "🥈", 3: "🥉" };
    return (
        <Surface elevation={0} sx={{ overflow: "hidden" }}>
            <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, borderBottom: `1px solid ${SM.border}`, display: "flex", alignItems: "center", gap: 1 }}>
                <LeaderboardOutlined sx={{ fontSize: 16, color: SM.teal }} />
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: SM.txt1, fontFamily: SM.sans }}>Leaderboard</Typography>
            </Box>
            <Box sx={{ overflowX: "auto" }}>
                <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontFamily: SM.sans }}>
                    <Box component="thead">
                        <Box component="tr" sx={{ background: SM.page }}>
                            {["#", "SM", "Calls", "Answered", "Rate", "Avg Duration", "Score", "Performance", ""].map(h => (
                                <Box component="th" key={h} sx={{
                                    px: { xs: 1, sm: 1.5 }, py: 1, fontSize: 10.5, fontWeight: 700,
                                    color: SM.txt4, textAlign: "left", whiteSpace: "nowrap",
                                    borderBottom: `1px solid ${SM.border}`,
                                }}>{h}</Box>
                            ))}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {smStats.map(sm => (
                            <Box component="tr" key={sm.userId} onClick={() => onSelect(sm)}
                                sx={{ cursor: "pointer", borderBottom: `1px solid ${SM.border2}`, "&:hover": { background: SM.tealSoft }, transition: "background .1s" }}>
                                <Box component="td" sx={{ px: { xs: 1, sm: 1.5 }, py: 1, fontSize: 13, fontWeight: 700, color: SM.teal, whiteSpace: "nowrap" }}>
                                    {MEDAL[sm.rank] || sm.rank}
                                </Box>
                                <Box component="td" sx={{ px: { xs: 1, sm: 1.5 }, py: 1, minWidth: 120 }}>
                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                        <Box sx={{
                                            width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                                            background: avColor(sm.agentName), color: "#fff",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            fontSize: 10, fontWeight: 700,
                                        }}>{initials(sm.agentName)}</Box>
                                        <Typography sx={{
                                            fontSize: { xs: 12, sm: 12.5 }, fontWeight: 600, color: SM.txt1,
                                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 100,
                                        }}>{sm.agentName}</Typography>
                                    </Stack>
                                </Box>
                                {[sm.totalCalls, sm.answered, `${sm.answerRate}%`, fmtDur(sm.avgDurationSec), sm.avgLeadScore].map((v, i) => (
                                    <Box component="td" key={i} sx={{ px: { xs: 1, sm: 1.5 }, py: 1, fontSize: { xs: 12, sm: 12.5 }, color: SM.txt2, whiteSpace: "nowrap" }}>{v}</Box>
                                ))}
                                <Box component="td" sx={{ px: { xs: 1, sm: 1.5 }, py: 1 }}>
                                    <Box sx={{
                                        px: "8px", py: "2px", borderRadius: 20, fontSize: 11, fontWeight: 700, display: "inline-block",
                                        background: scoreSoft(sm.performanceScore), color: scoreColor(sm.performanceScore),
                                    }}>{sm.performanceScore}</Box>
                                </Box>
                                <Box component="td" sx={{ px: { xs: 1, sm: 1.5 }, py: 1 }}>
                                    <Button size="small" onClick={e => { e.stopPropagation(); onSelect(sm); }}
                                        sx={{
                                            fontSize: 11, textTransform: "none", fontFamily: SM.sans, fontWeight: 600,
                                            color: SM.teal, background: SM.tealSoft, border: `1px solid ${SM.tealBd}`,
                                            borderRadius: 6, px: 1, py: 0.25, minWidth: 0,
                                            "&:hover": { background: SM.teal, color: "#fff" },
                                        }}>View</Button>
                                </Box>
                            </Box>
                        ))}
                        {smStats.length === 0 && (
                            <Box component="tr">
                                <Box component="td" colSpan={9} sx={{ textAlign: "center", py: 4, fontSize: 13, color: SM.txt4, fontFamily: SM.sans }}>
                                    No data for selected period
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Surface>
    );
}

/* ══════════════════════════════════════════════════════
   INSIGHT PANEL
══════════════════════════════════════════════════════ */
function SMInsightPanel({ smStats }) {
    const insights = useMemo(() => {
        if (!smStats.length) return [];
        const sorted  = [...smStats].sort((a, b) => b.performanceScore - a.performanceScore);
        const byAns   = [...smStats].sort((a, b) => b.answerRate - a.answerRate);
        const byMiss  = [...smStats].sort((a, b) => b.missed - a.missed);
        const byDur   = [...smStats].sort((a, b) => b.totalDurationSec - a.totalDurationSec);
        const list = [];
        if (sorted[0]) list.push({ type: "success", text: `${sorted[0].agentName} is the best performer with a score of ${sorted[0].performanceScore}` });
        if (byAns[0])  list.push({ type: "success", text: `${byAns[0].agentName} has the highest answer rate at ${byAns[0].answerRate}%` });
        if (byMiss[0] && byMiss[0].missed > 5) list.push({ type: "warning", text: `${byMiss[0].agentName} has the most missed calls (${byMiss[0].missed}) — needs follow-up` });
        if (byDur[0])  list.push({ type: "info",    text: `${byDur[0].agentName} logged the most talk time: ${fmtTalkTime(byDur[0].totalDurationSec)}` });
        const lowAns = smStats.filter(s => s.answerRate < 50 && s.totalCalls >= 5);
        if (lowAns.length) list.push({ type: "warning", text: `${lowAns.length} SM${lowAns.length > 1 ? "s" : ""} have answer rate below 50%: ${lowAns.map(s => s.agentName).join(", ")}` });
        const highDurLowScore = smStats.filter(s => s.avgDurationSec > 240 && s.avgLeadScore < 40 && s.totalCalls >= 5);
        if (highDurLowScore.length) list.push({ type: "warning", text: `${highDurLowScore[0].agentName} has high call duration but low lead score — review call quality` });
        if (sorted.length > 1) {
            const worst = sorted[sorted.length - 1];
            list.push({ type: "error", text: `${worst.agentName} needs improvement — performance score ${worst.performanceScore}` });
        }
        return list;
    }, [smStats]);

    const typeStyle = {
        success: { color: SM.green,  bg: SM.greenSoft,  bd: SM.greenBd },
        warning: { color: SM.amber,  bg: SM.amberSoft,  bd: SM.amberBd },
        error:   { color: SM.red,    bg: SM.redSoft,    bd: SM.redBd },
        info:    { color: SM.teal,   bg: SM.tealSoft,   bd: SM.tealBd },
    };

    return (
        <Surface elevation={0} sx={{ overflow: "hidden", height: "100%" }}>
            <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, borderBottom: `1px solid ${SM.border}`, display: "flex", alignItems: "center", gap: 1 }}>
                <Insights sx={{ fontSize: 16, color: SM.purple }} />
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: SM.txt1, fontFamily: SM.sans }}>AI Insights</Typography>
            </Box>
            <Box sx={{ p: { xs: 1.25, sm: 1.5 }, display: "flex", flexDirection: "column", gap: 1 }}>
                {insights.length === 0 && (
                    <Typography sx={{ color: SM.txt4, fontSize: 13, textAlign: "center", py: 3, fontFamily: SM.sans }}>No data yet</Typography>
                )}
                {insights.map((ins, i) => {
                    const s = typeStyle[ins.type] || typeStyle.info;
                    return (
                        <Box key={i} sx={{ p: "10px 12px", borderRadius: 9, background: s.bg, border: `1px solid ${s.bd}` }}>
                            <Typography sx={{ fontSize: { xs: 12, sm: 12.5 }, color: s.color, fontWeight: 500, lineHeight: 1.5, fontFamily: SM.sans }}>
                                {ins.text}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Surface>
    );
}

/* ══════════════════════════════════════════════════════
   EXPORT BUTTON
══════════════════════════════════════════════════════ */
function ExportReportButton({ smStats }) {
    const [anchor, setAnchor] = useState(null);

    const exportCSV = (rows, filename) => {
        const headers = ["SM Name", "Total Calls", "Answered", "Missed", "Not Answered", "Connected", "Total Duration", "Avg Duration", "Answer Rate", "Missed Rate", "Avg Lead Score", "Performance Score", "Rank"];
        const lines = [
            headers.join(","),
            ...rows.map(s => [
                `"${s.agentName}"`, s.totalCalls, s.answered, s.missed, s.notAnswered, s.connected,
                fmtTalkTime(s.totalDurationSec), fmtDur(s.avgDurationSec),
                `${s.answerRate}%`, `${s.missedRate}%`, s.avgLeadScore, s.performanceScore, s.rank,
            ].join(",")),
        ];
        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <Button size="small" startIcon={<Download sx={{ fontSize: 15 }} />}
                onClick={e => setAnchor(e.currentTarget)}
                sx={{
                    textTransform: "none", fontFamily: SM.sans, fontWeight: 600, fontSize: 12.5,
                    background: SM.teal, color: "#fff", borderRadius: 8, px: 1.5,
                    "&:hover": { background: "#151fa5" },
                }}>
                Export
            </Button>
            <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ sx: { mt: 0.75, borderRadius: 2, border: `1px solid ${SM.border}`, boxShadow: "0 8px 24px rgba(0,0,0,.1)", minWidth: 220 } }}>
                {[
                    { label: "Export All SM Summary",    fn: () => exportCSV(smStats, "sm-summary.csv") },
                    { label: "Export Comparison Report", fn: () => exportCSV(smStats, "sm-comparison.csv") },
                ].map(item => (
                    <Box key={item.label} onClick={() => { item.fn(); setAnchor(null); }}
                        sx={{ px: 2, py: 1.25, cursor: "pointer", fontSize: 13, fontFamily: SM.sans, color: SM.txt2, "&:hover": { background: SM.tealSoft, color: SM.teal } }}>
                        {item.label}
                    </Box>
                ))}
            </Popover>
        </>
    );
}

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
export default function SMAnalyticsDashboard({
    visibleSmIds, isRootUser, isLeafUser, callLogsShowMode, loggedInUserId, smOptions,
}) {
    const [rows, setRows]             = useState([]);
    const [loading, setLoading]       = useState(false);
    const [datePreset, setDatePreset] = useState("thisMonth");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo]     = useState("");
    const [smSearch, setSmSearch]     = useState("");
    const [filters, setFilters]       = useState({
        callStatus: [], direction: [], callConnected: null,
        campaignName: [], leadTemperature: [], callType: [],
        minDuration: null, maxDuration: null,
    });
    const [selectedSm, setSelectedSm] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const hierarchyLoading = visibleSmIds === null && callLogsShowMode !== "All";

    const fetchData = useCallback(async () => {
        if (hierarchyLoading) return;
        setLoading(true);
        try {
            const { from, to } = getDateRange(datePreset, customFrom, customTo);
            let q = supabase.from("CallLogs").select("*")
                .gte("startedAt", from)
                .lte("startedAt", to)
                .order("startedAt", { ascending: false });

            /* Mirror exact scoping logic from CallLogsDashboard */
            if (callLogsShowMode === "All") {
                /* no filter — role config says see everything */
            } else if (isRootUser) {
                /* root of org — no filter, sees everything */
            } else {
                /* mid-level or leaf — scope to visible leaf IDs */
                if (!visibleSmIds || visibleSmIds.length === 0) {
                    setRows([]); setLoading(false); return;
                }
                q = q.in("userId", visibleSmIds);
            }

            const { data, error } = await q;
            if (error) { console.error("SMAnalytics fetch error:", error); throw error; }
            setRows(data || []);
        } catch (e) { console.error("SMAnalytics fetchData error:", e); }
        finally { setLoading(false); }
    }, [hierarchyLoading, isRootUser, visibleSmIds, datePreset, customFrom, customTo, callLogsShowMode]);

    useEffect(() => { fetchData(); }, [fetchData]);

    /* ── Apply client-side filters ── */
    const filtered = useMemo(() => {
        let r = rows;
        if (filters.callStatus.length)      r = r.filter(x => filters.callStatus.includes(x.callStatus));
        if (filters.direction.length)       r = r.filter(x => filters.direction.includes(x.direction));
        if (filters.callConnected !== null) r = r.filter(x => x.callConnected === filters.callConnected);
        if (filters.campaignName.length)    r = r.filter(x => filters.campaignName.includes(x.campaignName));
        if (filters.leadTemperature.length) r = r.filter(x => filters.leadTemperature.includes(x.aiLeadTemperature));
        if (filters.callType.length)        r = r.filter(x => filters.callType.includes(x.aiCallType));
        if (filters.minDuration != null)    r = r.filter(x => (x.durationSec || 0) >= filters.minDuration);
        if (filters.maxDuration != null)    r = r.filter(x => (x.durationSec || 0) <= filters.maxDuration);
        return r;
    }, [rows, filters]);

    /* ── Per-SM stats ── */
    const smStats = useMemo(() => {
        const map = {};
        filtered.forEach(r => {
            const key = r.userId || r.agentName || "unknown";
            if (!map[key]) map[key] = {
                agentName: r.agentName || "Unknown",
                userId: r.userId || key,
                answered: 0, missed: 0, notAnswered: 0, connected: 0,
                totalDurationSec: 0, totalBillSec: 0,
                leadScores: [], callsByHour: Array(24).fill(0),
                callsByDay: { Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0 },
                leadTempDist: { hot: 0, warm: 0, cold: 0 },
                callTypeDist: {}, directionDist: { inbound: 0, outbound: 0, clicktocall: 0 },
                campaigns: new Set(), rawLogs: [],
            };
            const sm = map[key];
            sm.rawLogs.push(r);
            if (r.callStatus === "answered")         sm.answered++;
            else if (r.callStatus === "missed")      sm.missed++;
            else if (r.callStatus === "not answered") sm.notAnswered++;
            if (r.callConnected === true) sm.connected++;
            sm.totalDurationSec += r.durationSec || 0;
            sm.totalBillSec     += r.billSec     || 0;
            if (r.aiLeadScore != null) sm.leadScores.push(r.aiLeadScore);
            if (r.startedAt) {
                const d = new Date(r.startedAt);
                sm.callsByHour[d.getHours()]++;
                const DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
                sm.callsByDay[DAY[d.getDay()]] = (sm.callsByDay[DAY[d.getDay()]] || 0) + 1;
            }
            const temp = (r.aiLeadTemperature || "").toLowerCase();
            if (temp === "hot" || temp === "warm" || temp === "cold") sm.leadTempDist[temp]++;
            if (r.aiCallType) sm.callTypeDist[r.aiCallType] = (sm.callTypeDist[r.aiCallType] || 0) + 1;
            const dir = (r.direction || "").toLowerCase();
            if (["inbound", "outbound", "clicktocall"].includes(dir)) sm.directionDist[dir]++;
            if (r.campaignName) sm.campaigns.add(r.campaignName);
        });

        const list = Object.values(map).map(sm => {
            const total = sm.answered + sm.missed + sm.notAnswered;
            const answerRate   = pct(sm.answered, total);
            const missedRate   = pct(sm.missed,   total);
            const avgDuration  = sm.answered > 0 ? Math.round(sm.totalDurationSec / sm.answered) : 0;
            const avgBill      = sm.answered > 0 ? Math.round(sm.totalBillSec     / sm.answered) : 0;
            const avgLeadScore = sm.leadScores.length
                ? Math.round(sm.leadScores.reduce((a, b) => a + b, 0) / sm.leadScores.length) : 0;
            const durationScore    = Math.min(100, Math.round((avgDuration / 300) * 100));
            const performanceScore = Math.round(answerRate * 0.4 + avgLeadScore * 0.3 + durationScore * 0.3);
            return {
                ...sm,
                totalCalls: total, answerRate, missedRate,
                avgDurationSec: avgDuration, avgBillSec: avgBill,
                avgLeadScore, durationScore, performanceScore,
                campaigns: [...sm.campaigns],
            };
        });

        list.sort((a, b) => b.performanceScore - a.performanceScore);
        list.forEach((sm, i) => { sm.rank = i + 1; });
        return list;
    }, [filtered]);

    const visibleStats = useMemo(() =>
        smSearch.trim()
            ? smStats.filter(s => s.agentName.toLowerCase().includes(smSearch.toLowerCase()))
            : smStats,
        [smStats, smSearch]
    );

    const overall = useMemo(() => {
        const totCalls = smStats.reduce((s, x) => s + x.totalCalls, 0);
        const totAns   = smStats.reduce((s, x) => s + x.answered,   0);
        const totMiss  = smStats.reduce((s, x) => s + x.missed,     0);
        const totNotAns= smStats.reduce((s, x) => s + x.notAnswered,0);
        const totConn  = smStats.reduce((s, x) => s + x.connected,  0);
        const totDur   = smStats.reduce((s, x) => s + x.totalDurationSec, 0);
        const totBill  = smStats.reduce((s, x) => s + x.totalBillSec,     0);
        const avgDur   = totAns > 0 ? Math.round(totDur / totAns) : 0;
        const avgBill  = totAns > 0 ? Math.round(totBill / totAns) : 0;
        const mostActive = smStats.length ? smStats.reduce((a, b) => a.totalCalls >= b.totalCalls ? a : b) : {};
        const bestPerf   = smStats[0] || {};
        const highDur    = smStats.length ? [...smStats].sort((a, b) => b.totalDurationSec - a.totalDurationSec)[0] : {};
        const n = smStats.length || 1;
        const avgLeadScore  = smStats.length ? Math.round(smStats.reduce((s, x) => s + (x.avgLeadScore || 0), 0) / smStats.length) : 0;
        const avgPerfScore  = smStats.length ? Math.round(smStats.reduce((s, x) => s + (x.performanceScore || 0), 0) / smStats.length) : 0;
        return {
            totCalls, totAns, totMiss, totNotAns, totConn, totDur, avgDur, avgBill,
            answerRate: pct(totAns,  totCalls),
            missedRate: pct(totMiss, totCalls),
            mostActiveName: mostActive.agentName || "—",
            bestPerfName:   bestPerf.agentName   || "—",
            highDurName:    highDur.agentName     || "—",
            /* Fields used by SMComparisonPanel for team averages */
            totalSMs: smStats.length,
            totalCalls: totCalls,
            answered: totAns, missed: totMiss, notAnswered: totNotAns, connected: totConn,
            avgDurationSec: avgDur, avgBillSec: avgBill,
            avgLeadScore, avgPerfScore,
        };
    }, [smStats]);

    const isPageLoading = loading || hierarchyLoading;

    /* ── Render ── */
    return (
        <Box sx={{ fontFamily: SM.sans }}>
            {isPageLoading && (
                <LinearProgress sx={{ height: 2, background: SM.tealSoft, "& .MuiLinearProgress-bar": { background: SM.teal } }} />
            )}

            {/* PAGE HEADER */}
            <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", mb: { xs: 1.5, sm: 2 }, flexWrap: "wrap", gap: 1 }}>
                <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 22 }, color: SM.txt1, fontFamily: SM.sans, letterSpacing: "-.4px" }}>
                        Sales Manager Call Analytics
                    </Typography>
                    <Typography sx={{ fontSize: { xs: 11.5, sm: 13 }, color: SM.txt3, mt: 0.2, fontFamily: SM.sans }}>
                        Review SM-wise call activity, duration, conversion signals, and performance comparison
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <ExportReportButton smStats={smStats} />
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchData} disabled={isPageLoading}
                            sx={{ border: `1px solid ${SM.border}`, borderRadius: 8, color: SM.teal, background: SM.white, "&:hover": { background: SM.tealSoft } }}>
                            <Refresh sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            {/* DATE RANGE */}
            <Box sx={{ mb: { xs: 1.25, sm: 1.5 } }}>
                <DateRangeBar preset={datePreset} onPreset={setDatePreset}
                    customFrom={customFrom} customTo={customTo}
                    onCustomFrom={setCustomFrom} onCustomTo={setCustomTo} />
            </Box>

            {/* SEARCH + FILTER ROW */}
            <Box sx={{ display: "flex", gap: 1, mb: { xs: 1.5, sm: 2 }, flexWrap: "wrap", alignItems: "center" }}>
                <TextField size="small" placeholder="Search Sales Manager…" value={smSearch}
                    onChange={e => setSmSearch(e.target.value)}
                    sx={{
                        flex: 1, minWidth: { xs: "100%", sm: 240 }, maxWidth: { sm: 320 },
                        "& .MuiOutlinedInput-root": {
                            fontFamily: SM.sans, fontSize: 13.5, borderRadius: 8,
                            "& fieldset": { borderColor: SM.border },
                            "&:hover fieldset": { borderColor: SM.tealBd },
                            "&.Mui-focused fieldset": { borderColor: SM.teal },
                        },
                    }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: SM.txt4 }} /></InputAdornment>,
                        endAdornment: smSearch ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSmSearch("")}><Close sx={{ fontSize: 13 }} /></IconButton></InputAdornment> : null,
                    }}
                />
            </Box>

            {/* LEAF USER BANNER */}
            {isLeafUser && smStats.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ px: "12px", py: "8px", borderRadius: 9, background: SM.tealSoft, border: `1px solid ${SM.tealBd}` }}>
                        <Typography sx={{ fontSize: 12.5, color: SM.teal, fontFamily: SM.sans }}>
                            Showing your personal call analytics. Click your card below to see full analysis.
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* KPI CARDS */}
            <SMKpiCards overall={overall} loading={isPageLoading} />

            {/* SM PERFORMANCE CARDS */}
            <SMPerformanceCards
                smStats={visibleStats}
                onSelect={sm => { setSelectedSm(sm); setDrawerOpen(true); }}
                loading={isPageLoading}
            />

            {/* LEADERBOARD */}
            {smStats.length > 0 && (
                <Box sx={{ mt: { xs: 0.5, sm: 1 } }}>
                    <SMLeaderboard smStats={smStats} onSelect={sm => { setSelectedSm(sm); setDrawerOpen(true); }} />
                </Box>
            )}

            {/* AI INSIGHTS */}
            {smStats.length > 0 && (
                <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
                    <SMInsightPanel smStats={smStats} />
                </Box>
            )}

            {/* DETAIL DRAWER */}
            <SMDetailDrawer
                open={drawerOpen}
                sm={selectedSm}
                allSmStats={smStats}
                overall={overall}
                onClose={() => { setDrawerOpen(false); setSelectedSm(null); }}
            />
        </Box>
    );
}
